/**
 * ErrorBanner — structured error display for QlipperError-shaped failures.
 *
 * Replaces the inline plain-text error rendering with:
 *   - Error code badge (e.g., AI_NO_CLIPS, RENDER_INPUT_MISSING) for support tickets
 *   - ui_message as the primary user-facing text
 *   - Category icon (config / capability / bug / network)
 *   - Retryability hint
 *   - Action buttons sourced from `remediations[]` — each remediation has
 *     {action, label, args?} where the `action` string maps to a callback
 *     provided by the parent (e.g., 'open-settings', 'install-whisper',
 *     'manual'). Unknown actions fall through to a no-op render so the user
 *     at least sees the suggestion text.
 *
 * Designed to coexist with the legacy plain-string `pipelineError`. When
 * structured `errorInfo` is present, ErrorBanner renders; otherwise the
 * caller falls back to a plain banner.
 */

import React from 'react';
import { AlertCircle, AlertTriangle, Info, RotateCcw, Settings as SettingsIcon, ExternalLink } from 'lucide-react';

const CATEGORY_META = {
    config:     { icon: SettingsIcon,  color: 'amber',  label: 'Configuration' },
    capability: { icon: AlertTriangle, color: 'amber',  label: 'Capability' },
    network:    { icon: AlertTriangle, color: 'amber',  label: 'Network' },
    bug:        { icon: AlertCircle,   color: 'red',    label: 'Bug' },
};

const RETRYABLE_LABEL = {
    auto:   'Will retry automatically',
    manual: 'Click Retry to try again',
    never:  'Cannot be retried — fix the underlying issue first',
};

function ErrorBanner({ errorInfo, onResume, onRetry, onAction, isResumable, isRunning }) {
    if (!errorInfo) return null;

    const { code, ui_message, category = 'bug', retryable = 'manual', remediations = [] } = errorInfo;
    const meta = CATEGORY_META[category] || CATEGORY_META.bug;
    const Icon = meta.icon;
    const palette = meta.color === 'amber'
        ? { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-500', btn: 'bg-amber-500 hover:bg-amber-600' }
        : { bg: 'bg-red-500/10',   border: 'border-red-500/20',   text: 'text-red-500',   btn: 'bg-red-500 hover:bg-red-600' };

    return (
        <div className={`flex items-start gap-3 p-3 rounded-xl ${palette.bg} border ${palette.border} ${palette.text}`}>
            <Icon size={16} className="flex-shrink-0 mt-0.5" />

            <div className="flex-1 min-w-0 flex flex-col gap-2">
                {/* Header text */}
                <div>
                    <p className="text-[12px] font-medium leading-snug">{ui_message || 'An error occurred.'}</p>
                    {code && (
                        <p className="text-[10px] opacity-60 mt-0.5 font-mono">
                            {code} · {meta.label} · {RETRYABLE_LABEL[retryable] || retryable}
                        </p>
                    )}
                </div>

                {/* Remediations — collapsed to a single one-line summary by default,
                    expandable via <details>. Avoids the "wall of chips" when yt-dlp
                    returns 4-5 long suggestions. */}
                {remediations.length > 0 && (
                    <details className="text-[11px]">
                        <summary className="cursor-pointer opacity-75 hover:opacity-100 select-none">
                            {remediations.length} suggestion{remediations.length > 1 ? 's' : ''} ▾
                        </summary>
                        <ul className="mt-1.5 space-y-1 pl-1">
                            {remediations.map((rem, i) => {
                                const handler = onAction ? () => onAction(rem) : null;
                                const label = rem.label || rem.action || `Suggestion ${i + 1}`;
                                if (rem.action === 'manual' || !handler) {
                                    return (
                                        <li key={i} className="flex items-start gap-1.5 leading-snug">
                                            <Info size={10} className="flex-shrink-0 mt-0.5 opacity-60" />
                                            <span>{label}</span>
                                        </li>
                                    );
                                }
                                return (
                                    <li key={i}>
                                        <button
                                            onClick={handler}
                                            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${palette.btn} text-white transition-all`}
                                            title={`Action: ${rem.action}`}
                                        >
                                            <ExternalLink size={10} /> {label}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </details>
                )}
            </div>

            {/* Action buttons inline-right — no divider, tighter layout */}
            {(isResumable || onRetry) && (
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                    {isResumable && (
                        <button
                            onClick={onResume}
                            disabled={isRunning}
                            className="px-3 py-1.5 rounded-lg bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-white text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5 transition-all"
                            title="Continue this job from the last completed phase"
                        >
                            <RotateCcw size={11} /> Resume
                        </button>
                    )}
                    {onRetry && (
                        <button
                            onClick={onRetry}
                            disabled={isRunning || retryable === 'never'}
                            className={`px-3 py-1.5 rounded-lg ${palette.btn} disabled:opacity-50 text-white text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5 transition-all`}
                        >
                            <RotateCcw size={11} /> Retry
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default ErrorBanner;
